<template>
  <div id="cells">
    <Toolbar :title="t('Cells')" :backButton="editing" @back="back">
      <div slot="buttons">
        <v-btn icon @click="validateSave" v-show="editing" :disabled="!dirty || !valid || readOnly">
          <v-icon>save</v-icon>
        </v-btn>

        <v-btn icon @click="select" v-show="editing">
          <v-icon>format_list_bulleted</v-icon>
        </v-btn>

        <v-checkbox v-model="showArchived" v-show="!editing" color="accent" class="mt-3"></v-checkbox>
      </div>
    </Toolbar>

    <v-list v-show="!editing">
      <v-list-tile v-for="cell in sorted" :key="cell['.key']" @click="editExisting(cell)" :class="{ disabled: cell.archived }">
        <v-list-tile-avatar size="40px">
          <v-icon class="secondary">home</v-icon>
        </v-list-tile-avatar>

        <v-list-tile-content>
          <v-list-tile-title v-html="cell.desc"> </v-list-tile-title>
        </v-list-tile-content>

        <v-list-tile-action v-html="cell['.key']"> </v-list-tile-action>
      </v-list-tile>
    </v-list>

    <!-- Edit -->
    <v-form id="cell" v-model="valid" ref="form" v-show="editing">
      <v-card-text>
        <v-text-field
          :label="t('Symbol')"
          v-model="name"
          :disabled="!isCreatingNew"
          :rules="isCreatingNew ? [rules.required, ...cellNameValidation] : []"
          @input="dirty = true"
        ></v-text-field>

        <v-select
          :label="t('Type')"
          v-model="type"
          :items="cellTypes"
          :disabled="!isCreatingNew"
          :rules="[rules.required]"
          @input="dirty = true"
        ></v-select>

        <v-select
          :label="t('Parent Cell')"
          v-model="parent"
          :items="parentCells"
          item-text="desc"
          item-value="key"
          v-if="type == 'O'"
          :rules="[rules.required]"
          @input="dirty = true"
        ></v-select>

        <v-text-field :label="t('Description')" v-model="desc" @input="dirty = true"></v-text-field>
        <v-text-field :label="t('Email report to')" v-model="reportEmails" @input="dirty = true"></v-text-field>

        <v-checkbox
          :label="t('Archived')"
          v-model="archived"
          @change="dirty = true"
        ></v-checkbox>
      </v-card-text>
    </v-form>

    <!-- Add new cell button -->
    <v-btn
      fab
      fixed
      bottom
      right
      color="primary"
      @click="addNewCell"
      v-show="!editing && !readOnly"
    >
      <v-icon>add</v-icon>
    </v-btn>
  </div>
</template>

<script>
import Toolbar from '@/components/layout/Toolbar'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { mapStateMutations, mapGettersMutations } from '@/store'

export default {
  components: { Toolbar },

  async created() {
    await this.$store.dispatch('authorize')
    await this.$store.dispatch('cell/names')
    this.deselect()
  },

  data() {
    return {
      showArchived: false,
      isCreatingNew: false,
      cellTypes: [
        { text: 'L', value: 'L' },
        { text: 'O', value: 'O' }
      ],
      rules: {
        required: v => !!v || this.t('Required')
      }
    }
  },

  computed: {
    ...mapState('cell', ['cells', 'items']),
    ...mapGetters('cell', ['isNew', 'buttons', 'validations']),
    ...mapGetters(['readOnly']),
    editing: mapStateMutations('cell/editing'),
    dirty: mapStateMutations('cell/dirty'),
    valid: mapStateMutations('cell/valid'),
    name: mapGettersMutations('cell/name'),
    type: mapGettersMutations('cell/type'),
    desc: mapGettersMutations('cell/desc'),
    parent: mapGettersMutations('cell/parent'),
    reportEmails: mapGettersMutations('cell/reportEmails'),
    archived: mapGettersMutations('cell/archived'),
    sorted() {
      const a = this.items.slice().sort((a, b) => {
        const da = this.cellDesc(a['.key'])
        const db = this.cellDesc(b['.key'])
        return da.localeCompare(db, 'pl')
      })
      return this.showArchived ? a : a.filter(it => !it.archived)
    },
    parentCells() {
      return this.items
        .filter(cell => cell.type === 'L' && !cell.archived)
        .map(cell => ({
          key: cell['.key'],
          desc: this.cellDesc(cell['.key'])
        }))
        .sort((a, b) => a.desc.localeCompare(b.desc, 'pl'))
    },
    cellNameValidation() {
      return this.validations.cellName || []
    }
  },

  methods: {
    ...mapMutations('cell', ['edit']),
    ...mapActions('cell', [
      'bind',
      'back',
      'changeName',
      'changeRole',
      'askSetEnd',
      'changeEnd',
      'save',
      'select',
      'deselect',
      'remove',
      'finish',
      'beforeRouteLeave',
    ]),
    cellDesc(cell) {
      return this.$store.getters['cell/cellDesc'](cell)
    },
    async validateSave() {
      this.$refs.form.validate()
      if (this.valid) {
        await this.save()
        await this.$store.dispatch('cell/names')
        this.isCreatingNew = false
      }
    },
    addNewCell() {
      this.isCreatingNew = true
      this.edit({})
    },
    editExisting(cell) {
      this.isCreatingNew = false
      this.edit(cell)
    },
  },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave({
      to,
      from,
      next,
    })
  },
}
</script>

<style lang="stylus"></style>
